import React, { useContext, forwardRef, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { TranslatorContext } from '../context/Translator';
import { SidebarContext } from '../context/Sidebar';
import sidenavs from '../assets/data/sidenavs.json';
import { clearTokenFromLocalStorage } from '../utils/auth';
import { usePendingApprovals } from '../context/PendingApprovals';

const isPathActive = (path, pathname) => path === pathname || pathname.startsWith(`${path}/`);

/**
 * #231533 — Chấm đỏ + số việc đang chờ duyệt cho một mục menu.
 *
 * Mục lá khai `"pendingKey"` trong `sidenavs.json`; mục cha CỘNG DỒN số của mọi mục con (đệ quy)
 * nên chấm đỏ vẫn thấy được khi submenu đang đóng — đó chính là lỗ hổng của #223612: màn duyệt nằm
 * ở mục thứ 4/5 trong submenu "Nhập hàng & Nhà cung cấp", Admin không mở ra thì không bao giờ biết.
 */
const pendingCountOf = (menu, counts) => {
  if (menu.pendingKey) return Number(counts[menu.pendingKey] ?? 0);
  if (!menu.submenus) return 0;
  return menu.submenus.reduce((sum, sub) => sum + pendingCountOf(sub, counts), 0);
};

function PendingBadge({ count }) {
  if (!count) return null;
  return (
    <sup
      className="red mc-sidebar-pending-badge"
      title={`${count} yêu cầu đang chờ duyệt`}
    >
      {count}
    </sup>
  );
}

// menu.path (lá) hoặc menu.submenus (nhóm, có thể lồng nhiều tầng) — đệ quy để tìm route đang mở
// nằm ở đâu, dùng để tự bật sáng + tự mở nhóm cha (yêu cầu #194334 cho menu "Quản lý kho").
const menuHasActivePath = (menu, pathname) => {
  if (menu.path) return isPathActive(menu.path, pathname);
  if (!menu.submenus) return false;
  return menu.submenus.some((sub) => menuHasActivePath(sub, pathname));
};

// Tầng thứ 3 trở lên trong 1 dropdown (vd "Nguyên vật liệu" bên trong "Quản lý kho"): tự mở khi có
// mục con đang active, người dùng vẫn bấm mở/đóng tay được sau đó.
function SidebarSubgroup({ group, pathname, t, counts }) {
  const isGroupActive = menuHasActivePath(group, pathname);
  const [open, setOpen] = useState(isGroupActive);
  const liRef = useRef(null);

  useEffect(() => {
    if (isGroupActive) setOpen(true);
  }, [isGroupActive]);

  // Nhóm con (tầng 3) vừa mở/đóng làm đổi chiều cao nội dung — báo lại khung tầng 2 (top-level
  // dropdown-list, đang giữ chiều cao cố định bằng px) đo lại, không thì các mục dưới nó (vd "Kiểm
  // kê", "Báo cáo kho") sẽ bị cắt cụt (#194334).
  useEffect(() => {
    const ancestorList = liRef.current?.closest('.mc-sidebar-dropdown-list');
    if (ancestorList && ancestorList.style.height && ancestorList.style.height !== '0px') {
      ancestorList.style.height = `${ancestorList.scrollHeight}px`;
    }
  }, [open]);

  return (
    <li ref={liRef} className={`mc-sidebar-subgroup-item${open ? ' open' : ''}${isGroupActive ? ' active' : ''}`}>
      <button
        type="button"
        className="mc-sidebar-subgroup-btn"
        onClick={() => setOpen((o) => !o)}
      >
        <span>{t(group.text)}</span>
        <PendingBadge count={pendingCountOf(group, counts)} />
        <small className="material-icons">{open ? 'expand_less' : 'expand_more'}</small>
      </button>
      <ul className="mc-sidebar-subgroup-list">
        {group.submenus.map((leaf, i) => (
          <li
            key={i}
            className={`mc-sidebar-dropdown-item${isPathActive(leaf.path, pathname) ? ' active' : ''}`}
          >
            <Link to={leaf.path} className="mc-sidebar-dropdown-link">
              {t(leaf.text)}
              <PendingBadge count={pendingCountOf(leaf, counts)} />
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
}

const flatMenus = sidenavs.flatMap((sidenav) => sidenav.menus);

const SidebarLayout = forwardRef((props, ref) => {
  const { t, n, currentLanguage } = useContext(TranslatorContext);
  const { sidebar } = useContext(SidebarContext);
  const location = useLocation();
  const { supplierOrderChanges, handoverRequests } = usePendingApprovals();
  const pendingCounts = { supplierOrderChanges, handoverRequests };

  const handleDropdown = (event) => {
    const buttonElement = event.currentTarget;
    const itemElement = buttonElement.parentElement;
    const isActive = itemElement.classList.contains('active');
    const allItems = document.querySelectorAll('.mc-sidebar-menu-item');
    const allDropdowns = document.querySelectorAll('.mc-sidebar-dropdown-list');
    const dropdownElement = itemElement.querySelector('.mc-sidebar-dropdown-list');

    if (isActive) {
      itemElement.classList.remove('active');
      dropdownElement.style.height = '0px';
    } else {
      allItems.forEach((item) => item.classList.remove('active'));
      allDropdowns.forEach((dropdown) => (dropdown.style.height = '0px'));

      itemElement.classList.add('active');
      dropdownElement.style.height = dropdownElement.scrollHeight + 'px';
    }
  };

  // Mở sẵn đúng nhóm menu cha đang chứa route hiện tại (vd đang ở /inventory/materials thì tự mở
  // nhóm "Quản lý kho" mà không cần bấm) — #194334.
  useEffect(() => {
    const allItems = document.querySelectorAll('.mc-sidebar-menu-item');
    allItems.forEach((item, idx) => {
      const menu = flatMenus[idx];
      if (!menu?.submenus) return;
      const dropdown = item.querySelector('.mc-sidebar-dropdown-list');
      if (menuHasActivePath(menu, location.pathname)) {
        item.classList.add('active');
        if (dropdown) dropdown.style.height = dropdown.scrollHeight + 'px';
      } else {
        item.classList.remove('active');
        if (dropdown) dropdown.style.height = '0px';
      }
    });
  }, [location.pathname]);

  return (
    <aside
      ref={ref}
      className={`mc-sidebar thin-scrolling ${sidebar && 'active'}`}
    >
      {sidenavs?.map((sidenav, sidenavIndex) => (
        <menu
          key={sidenavIndex}
          className="mc-sidebar-menu"
        >
          <h5 className="mc-sidebar-menu-title">{t(sidenav.title)}</h5>
          <ul className="mc-sidebar-menu-list">
            {sidenav.menus.map((menu, menuIndex) => (
              <li
                key={menuIndex}
                className="mc-sidebar-menu-item"
              >
                {menu.submenus ? (
                  <>
                    <button
                      type="button"
                      className="mc-sidebar-menu-btn"
                      onClick={handleDropdown}
                    >
                      <i className="material-icons">{menu.icon}</i>
                      <span>{t(menu.text)}</span>
                      {menu.badge?.text && <sup className={menu.badge.variant}>{t(menu.badge.text)}</sup>}
                      {menu.badge?.count && <sup className={menu.badge.variant}>{n(menu.badge.count)}</sup>}
                      <PendingBadge count={pendingCountOf(menu, pendingCounts)} />
                      {currentLanguage?.dir === 'ltr' ? (
                        <small className="material-icons right">chevron_right</small>
                      ) : (
                        <small className="material-icons left">chevron_left</small>
                      )}
                    </button>

                    <ul className="mc-sidebar-dropdown-list">
                      {menu.submenus.map((submenu, submenusIndex) => (
                        submenu.submenus ? (
                          <SidebarSubgroup
                            key={submenusIndex}
                            group={submenu}
                            pathname={location.pathname}
                            t={t}
                            counts={pendingCounts}
                          />
                        ) : (
                          <li
                            key={submenusIndex}
                            className={`mc-sidebar-dropdown-item${isPathActive(submenu.path, location.pathname) ? ' active' : ''}`}
                          >
                            <Link
                              to={submenu.path}
                              className="mc-sidebar-dropdown-link"
                            >
                              {t(submenu.text)}
                              <PendingBadge count={pendingCountOf(submenu, pendingCounts)} />
                            </Link>
                          </li>
                        )
                      ))}
                    </ul>
                  </>
                ) : (
                  <Link
                    to={menu.path}
                    className="mc-sidebar-menu-btn"
                  >
                    <i className="material-icons">{menu.icon}</i>
                    <span>{t(menu.text)}</span>
                    {menu.badge?.count && <sup className={menu.badge.variant}>{n(menu.badge.count)}</sup>}
                    {menu.badge?.text && <sup className={menu.badge.variant}>{t(menu.badge.text)}</sup>}
                    <PendingBadge count={pendingCountOf(menu, pendingCounts)} />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </menu>
      ))}

      <div className="mc-sidebar-logout text-center">
        <Link
          to="/login"
          onClick={clearTokenFromLocalStorage}
          className="mc-btn primary sm"
        >
          <i className="material-icons">lock</i>
          <span>{t('logout')}</span>
        </Link>
      </div>
    </aside>
  );
});

export default SidebarLayout;
